import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../services/api';

const IDLE_MINUTES = 5;
const MAX_ATTEMPTS = 3;

const LockScreen = () => {
  const [locked, setLocked] = useState(() => {
    return localStorage.getItem('lockState') === 'locked';
  });
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem('authToken');
  const isClockPage = location.pathname.startsWith('/clock/');
  const isLoginPage = location.pathname === '/login';

  // Only active when logged in and not on public/login pages
  const active = token && !isClockPage && !isLoginPage;

  const lock = useCallback(() => {
    setPin('');
    setError('');
    setAttempts(0);
    setLocked(true);
    localStorage.setItem('lockState', 'locked');
  }, []);

  const recordActivity = useCallback(() => {
    localStorage.setItem('lastActivity', Date.now().toString());
  }, []);

  // Sync lock/unlock across tabs
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'lockState') {
        if (e.newValue === 'locked') {
          setLocked(true);
        } else if (e.newValue === 'unlocked') {
          setLocked(false);
          setPin('');
          setError('');
          setAttempts(0);
        }
      }
      // If token was cleared (logout/failed PIN elsewhere), this tab should react too
      if (e.key === 'authToken' && !e.newValue) {
        setLocked(false);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Idle detection using a SHARED last-activity timestamp across tabs
  useEffect(() => {
    if (!active) return;
    const events = ['mousedown', 'keydown', 'touchstart', 'mousemove'];
    events.forEach(e => window.addEventListener(e, recordActivity));
    recordActivity();

    const checkIdle = setInterval(() => {
      if (locked) return;
      const last = parseInt(localStorage.getItem('lastActivity') || '0', 10);
      if (Date.now() - last > IDLE_MINUTES * 60 * 1000) {
        lock();
      }
    }, 10000);

    return () => {
      events.forEach(e => window.removeEventListener(e, recordActivity));
      clearInterval(checkIdle);
    };
  }, [active, locked, lock, recordActivity]);

  // Expose a global lock function so the header button can trigger it
  useEffect(() => {
    window.__lockScreen = lock;
    return () => { delete window.__lockScreen; };
  }, [lock]);

  const handleUnlock = useCallback(async () => {
    try {
      const response = await apiService.verifyPin({ pin });
      if (response.status === 200) {
        setLocked(false);
        setPin('');
        setAttempts(0);
        recordActivity();
        localStorage.setItem('lockState', 'unlocked');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 403 || (status === 401 && err.response?.data?.error === 'Invalid or expired token')) {
        localStorage.removeItem('authToken');
        navigate('/login');
        return;
      }
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setPin('');
      if (newAttempts >= MAX_ATTEMPTS) {
        localStorage.removeItem('authToken');
        navigate('/login');
      } else {
        setError(`Incorrect PIN (${MAX_ATTEMPTS - newAttempts} attempts left)`);
      }
    }
  }, [pin, attempts, navigate, recordActivity]);

  // Keyboard support while locked: type digits, Enter to submit, Backspace to delete
  useEffect(() => {
    if (!locked) return;

    const handleKey = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        setPin(prev => (prev.length < 6 ? prev + e.key : prev));
        setError('');
      } else if (e.key === 'Enter') {
        handleUnlock();
      } else if (e.key === 'Backspace') {
        setPin(prev => prev.slice(0, -1));
      } else if (e.key === 'Escape') {
        setPin('');
        setError('');
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [locked, handleUnlock]);

  const handleDigit = (d) => {
    if (pin.length < 6) {
      setPin(pin + d);
      setError('');
    }
  };

  const handleClear = () => { setPin(''); setError(''); };

  if (!locked || !active) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(20,20,20,0.98)', zIndex: 99999,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{ color: 'white', fontSize: '28px', marginBottom: '10px' }}>🔒 Locked</div>
      <div style={{ color: '#aaa', marginBottom: '20px' }}>Enter your PIN to unlock</div>

      <div style={{
        fontSize: '32px', letterSpacing: '10px', color: 'white',
        minHeight: '44px', marginBottom: '10px'
      }}>
        {pin.replace(/./g, '●')}
      </div>

      {error && <div style={{ color: '#ff6b6b', marginBottom: '15px' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 80px)', gap: '12px' }}>
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <button key={n} onClick={() => handleDigit(String(n))} style={padStyle}>{n}</button>
        ))}
        <button onClick={handleClear} style={{ ...padStyle, fontSize: '16px' }}>Clear</button>
        <button onClick={() => handleDigit('0')} style={padStyle}>0</button>
        <button onClick={handleUnlock} style={{ ...padStyle, background: '#28a745' }}>✓</button>
      </div>
    </div>
  );
};

const padStyle = {
  width: '80px', height: '80px', fontSize: '28px', fontWeight: 'bold',
  background: '#333', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer'
};

export default LockScreen;